import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { finalize, take, takeWhile, tap } from 'rxjs/operators';
import { merge, Subscription } from 'rxjs';
import { AppState } from '../../../../../../../core/reducers';
import {
  LayoutUtilsService,
  MessageType,
  QueryParamsModel,
} from '../../../../../../../core/_base/crud';
import { RelationshipEditDialogComponent } from '../../../contacts/_subs/relationships/relationship-edit/relationship-edit-dialog.component';
import { EntityRelationshipEditComponent } from '../../../doctors/relationships/relationship-edit/entity-relationship-edit.component';
import { AffiliationRelation } from '../../models';
import { AffiliationRelationDataSource } from '../../data-sources';
import {
  selectAffiliationRelationActionLoading,
  selectAffiliationRelationError,
} from '../../selectors';
import {
  CreateAffiliationRelationOnServer,
  DeleteAffiliationRelationOnServer,
  RequestAffiliationRelationPage,
  UpdateAffiliationRelationOnServer,
} from '../../actions';

@Component({
  selector: 'kt-affiliation-relation-list',
  templateUrl: './affiliation-relation-list.component.html',
  styleUrls: ['./affiliation-relation-list.component.scss'],
})
export class AffiliationRelationListComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();
  columns = [
    'relationType_category_name',
    'relationType_descriptionType',
    'classification',
    'actions',
  ];
  dataSource: AffiliationRelationDataSource;
  entityId: number;
  @Input() entityType: 'CONTACT' | 'AFFILIATION_GROUP';
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @Input() isViewMode;

  constructor(
    private route: ActivatedRoute,
    private store: Store<AppState>,
    private translate: TranslateService,
    private layoutUtils: LayoutUtilsService,
    public dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.entityId = this.route.snapshot.params.id;
    this.dataSource = new AffiliationRelationDataSource(this.store, this.paginator);
    this.columns.unshift(
      this.entityType.localeCompare('AFFILIATION_GROUP') === 0
        ? 'contact_firstName'
        : 'affiliationGroup_name',
    );

    this.subscriptions.add(this.dataSource.entitySubject.subscribe());

    this.subscriptions.add(
      this.sort.sortChange.subscribe((res) => {
        this.paginator.pageIndex = 0;

        if (!res.direction) {
          this.sort.direction = 'desc';
          this.sort.active = 'created';
        }
      }),
    );

    this.subscriptions.add(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(tap(() => this.loadAffiliationRelationList()))
        .subscribe(),
    );

    this.subscriptions.add(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => {
            const page = this.dataSource.affiliationRelations.slice(
              this.paginator.pageIndex * this.paginator.pageSize,
              Math.min(
                this.dataSource.affiliationRelations.length,
                (this.paginator.pageIndex + 1) * this.paginator.pageSize,
              ),
            );

            this.dataSource.entitySubject.next(page);
          }),
        )
        .subscribe(),
    );

    if (this.isViewMode) {
      this.columns = this.columns.filter((e) => e !== 'actions');
    }
    this.loadAffiliationRelationList();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadAffiliationRelationList(): void {
    const query = new QueryParamsModel(
      {},
      this.sort.direction ? this.sort.direction : 'desc',
      this.sort.active ? this.sort.active : 'created',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );

    this.store.dispatch(
      new RequestAffiliationRelationPage({
        entityId: this.entityId,
        entityType: this.entityType,
        query,
      }),
    );
  }

  displayMessage(message: string, messageType: MessageType): void {
    this.store
      .pipe(
        select(selectAffiliationRelationActionLoading),
        takeWhile((isLoading) => isLoading === true),
        finalize(() => {
          this.store.pipe(select(selectAffiliationRelationError), take(1)).subscribe((err) => {
            if (err) {
              const status = err.status;

              if (status === 401 || status === 403 || status === 404) return;

              message = this.translate.instant('GLOBAL.UNSPECIFIED_ERROR');
              return this.layoutUtils.showActionNotification(
                message,
                messageType,
                3000,
                false,
                false,
              );
            }

            this.layoutUtils.showActionNotification(message, messageType, 3000, false, false);
            setTimeout(() => this.loadAffiliationRelationList(), 500);
          });
        }),
      )
      .subscribe();
  }

  addAffiliationRelation(event: Event): void {
    event.stopPropagation();

    const affiliationRelation = new AffiliationRelation();
    this.updateAffiliationRelation(null, affiliationRelation);
  }

  updateAffiliationRelation(event: Event, relation: AffiliationRelation): void {
    if (event) event.stopPropagation();

    const message = this.translate.instant(
      `AFFILIATION.RELATIONS.${relation.id ? 'UPDATE_MESSAGE' : 'ADD_MESSAGE'}`,
    );
    const messageType = relation.id ? MessageType.Update : MessageType.Create;
    const dialogConfig = new MatDialogConfig();

    const isAffiliation = this.entityType.localeCompare('AFFILIATION_GROUP') === 0;
    const data: any = {
      entity_type: relation.relation_type?.category?.entity_type,
      relation_category_id: relation.relation_type?.category?.id,
      relation_type: relation.relation_type?.category?.name,
      relationship_description: relation.relation_type?.description_type,
      relation_type_id: relation.relation_type?.id,
      title: relation.title,
      is_prime: relation.is_prime,
      financial_responsible: relation.financial_responsible,
      emergency_contact: relation.emergency_contact,
      phi_authorized: relation.phi_authorized,
    };

    if (isAffiliation) data.contact = relation.contact;

    if (!isAffiliation) {
      data.entity_id = relation.affiliation_group?.id;
      data.first_name = relation.affiliation_group?.name;
    }

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '800px';
    dialogConfig.data = isAffiliation
      ? {
          contactEntity: false,
          entity_type: 'AFFILIATION_GROUP',
          entity_object: relation.id ? data : null,
          affiliation_id: this.entityId,
          fromAffiliation: true,
        }
      : {
          isAffiliationTab: true,
          contactEntity: false,
          contact: relation.id ? relation.contact : null,
          item: relation.id ? data : null,
        };

    const dialogRef = isAffiliation
      ? this.dialog.open(EntityRelationshipEditComponent, dialogConfig)
      : this.dialog.open(RelationshipEditDialogComponent, dialogConfig);

    this.subscriptions.add(
      dialogRef.afterClosed().subscribe((res) => {
        if (!res?.done && !res?.response) return;

        const response = res.response;
        const affiliationRelation = new AffiliationRelation(
          relation.id,
          { id: isAffiliation ? response.entity_id : this.entityId },
          { id: isAffiliation ? this.entityId : response.entity_id },
          { id: response.relation_type_id },
          response.title,
          response.is_prime,
          response.phi_authorized,
          response.emergency_contact,
          response.financial_responsible,
        );

        this.store.dispatch(
          relation.id
            ? new UpdateAffiliationRelationOnServer({ affiliationRelation })
            : new CreateAffiliationRelationOnServer({ affiliationRelation }),
        );
        this.displayMessage(message, messageType);
      }),
    );
  }

  deleteAffiliationRelation(event: Event, id: number): void {
    event.stopPropagation();

    const title = this.translate.instant('AFFILIATION.RELATIONS.DELETE.TITLE');
    const description = this.translate.instant('AFFILIATION.RELATIONS.DELETE.DESCRIPTION');
    const waitDescription = this.translate.instant('AFFILIATION.RELATIONS.DELETE.WAIT_DESCRIPTION');
    const message = this.translate.instant('AFFILIATION.RELATIONS.DELETE.MESSAGE');
    const messageType = MessageType.Delete;

    const dialogRef = this.layoutUtils.deleteElement(title, description, waitDescription);

    this.subscriptions.add(
      dialogRef.afterClosed().subscribe((res) => {
        if (!res) return;

        this.store.dispatch(new DeleteAffiliationRelationOnServer({ id }));
        this.displayMessage(message, messageType);
      }),
    );
  }

  getName(relation: AffiliationRelation): string {
    return this.entityType.localeCompare('AFFILIATION_GROUP') === 0
      ? relation.contact
        ? `${relation.contact.first_name} ${relation.contact.last_name}`
        : 'N/A'
      : relation.affiliation_group?.name || 'N/A';
  }
}
